import type { IdentityV1 } from '@alpod-ai-writer/backend';
import { LogOut } from 'lucide-react';
import { useNavigate } from 'react-router';

import { useApi } from '@/api/useApi.hook';
import { useAuthStore } from '@/auth/auth.store';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useI18n } from '@/i18n/useI18n';

import { LanguageSwitcher } from './LanguageSwitcher';

interface HeaderProps {
    identity: IdentityV1.OwnRecord;
}

export function Header({ identity }: HeaderProps) {
    const api = useApi();
    const navigate = useNavigate();
    const { clear } = useAuthStore();
    const { t } = useI18n();

    const handleLogout = () => {
        api.client.deleteSession();
        clear();
        navigate('/login', { replace: true });
    };

    const initials = identity.name
        ?.split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2) ?? '?';

    return (
        <header className="sticky top-0 z-20 flex h-14 items-center justify-end gap-3 border-b bg-background px-6">
            <LanguageSwitcher />

            <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center gap-2 outline-none">
                    <span className="text-sm text-muted-foreground hidden sm:inline">
                        {identity.name}
                    </span>
                    <Avatar className="h-8 w-8">
                        <AvatarImage src={identity.avatarUrl ?? undefined} />
                        <AvatarFallback>{initials}</AvatarFallback>
                    </Avatar>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                    <DropdownMenuLabel>
                        <div className="flex flex-col">
                            <span>{identity.name}</span>
                            <span className="text-xs font-normal text-muted-foreground">{identity.email}</span>
                        </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout}>
                        <LogOut className="mr-2 h-4 w-4" />
                        {t('header.logout')}
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        </header>
    );
}
